/**
 * AssignmentEmailComposer
 * Email controls for delegating a project or work order to an external user.
 *
 * The subject, message body, appended-content preview, and Reply-To live under a
 * collapsed "Anpassa e-postmeddelandet" section — hidden by default since the
 * defaults are usually fine. Only "Skicka en kopia till mig" is shown up front.
 *
 * The textarea holds the ENTIRE message body (prefilled with a default the admin
 * can fully rewrite). The read-only preview shows what the backend appends
 * automatically — the task info box, the action link, the overview link
 * (the personal link, for bookmarking) and the metadata footer.
 *
 * The link block (PersonalLinkBlock, always expanded) explains that one link
 * opens every task delegated to the recipient, lists what it opens today and
 * holds the validity choice (see useLinkValidityChoice).
 *
 * Controlled — state lives in the parent (see useAssignmentEmailOptions).
 */
import React, { useState } from 'react';
import { Form, Badge, Button, Collapse } from 'react-bootstrap';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { AssignmentEmailOptions } from '../hooks/useAssignmentEmailOptions';
import { LinkValidityChoice } from '../hooks/useLinkValidityChoice';
import { GuestTaskType } from '../types/guestAccess';
import { ExternalLinkInfo } from '../api/externalLinksApi';
import PersonalLinkBlock from './PersonalLinkBlock';

interface AssignmentEmailComposerProps {
    options: AssignmentEmailOptions;
    onSubjectChange: (value: string) => void;
    onCustomMessageChange: (value: string) => void;
    onReplyToChange: (value: string) => void;
    onSendCopyToAdminChange: (value: boolean) => void;
    /** Task type, for the preview labels and link text */
    taskType: GuestTaskType;
    /** Task title shown in the auto-appended info box */
    taskTitle: string;
    /** Building name shown in the auto-appended info box (optional) */
    buildingName?: string;
    /** Loading the default Reply-To address */
    loadingDefault?: boolean;
    disabled?: boolean;
    /** Unique suffix for the checkbox id when several composers can coexist */
    idSuffix?: string;
    /** The recipient's full name, for the link block */
    recipientName: string;
    /** The task being delegated (left out of the list of what the link opens) */
    taskKey?: string;
    /** The recipient's link, log and tasks (useExternalLink); null while unknown */
    linkInfo: ExternalLinkInfo | null;
    /** The admin's validity choice */
    link: LinkValidityChoice;
    /** True while the recipient's current link is being looked up */
    linkLoading?: boolean;
    /** Set when the lookup failed: the link is unknown, so no validity is offered */
    linkError?: string | null;
    onRetryLink?: () => void;
}

const AssignmentEmailComposer: React.FC<AssignmentEmailComposerProps> = ({
    options,
    onSubjectChange,
    onCustomMessageChange,
    onReplyToChange,
    onSendCopyToAdminChange,
    taskType,
    taskTitle,
    buildingName,
    loadingDefault,
    disabled,
    idSuffix = 'default',
    recipientName,
    taskKey,
    linkInfo,
    link,
    linkLoading,
    linkError,
    onRetryLink,
}) => {
    const [expanded, setExpanded] = useState(false);
    const isProject = taskType === 'project';
    const linkLabel = isProject ? 'Öppna projektet' : 'Visa och slutför ärende';

    return (
        <>
            <PersonalLinkBlock
                recipientName={recipientName}
                taskType={taskType}
                taskKey={taskKey}
                info={linkInfo}
                choice={link}
                loading={linkLoading}
                error={linkError}
                onRetry={onRetryLink}
                disabled={disabled}
                idSuffix={idSuffix}
            />

            <div
                className="d-flex align-items-center gap-1 mb-2 text-primary"
                style={{ cursor: 'pointer', userSelect: 'none', width: 'fit-content' }}
                role="button"
                aria-expanded={expanded}
                onClick={() => setExpanded((prev) => !prev)}
            >
                {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span className="fw-semibold">Anpassa e-postmeddelandet</span>
            </div>

            <Collapse in={expanded}>
                <div>
                    <Form.Group className="mb-3">
                        <Form.Label>Ämne</Form.Label>
                        <Form.Control
                            type="text"
                            value={options.subject}
                            onChange={(e) => onSubjectChange(e.target.value)}
                            placeholder="Ämnesrad för e-posten"
                            maxLength={200}
                            disabled={disabled}
                        />
                        <small className={`d-block mt-1 text-end ${options.subject.length >= 200 ? 'text-danger' : 'text-muted'}`}>
                            {options.subject.length} / 200
                        </small>
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>Meddelande till mottagaren</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={7}
                            value={options.customMessage}
                            onChange={(e) => onCustomMessageChange(e.target.value)}
                            placeholder="Skriv meddelandet som skickas i e-posten…"
                            maxLength={4000}
                            disabled={disabled}
                        />
                        <small className={`d-block mt-1 text-end ${options.customMessage.length >= 4000 ? 'text-danger' : 'text-muted'}`}>
                            {options.customMessage.length} / 4000
                        </small>
                    </Form.Group>

                    {/* Read-only preview of what the backend appends after the message. */}
                    <div className="border rounded p-3 bg-light mb-3">
                        <div
                            className="text-uppercase fw-semibold text-muted mb-2"
                            style={{ fontSize: '11px', letterSpacing: '0.04em' }}
                        >
                            Läggs till automatiskt längst ner
                        </div>
                        <div className="bg-white border rounded p-2 mb-2 small">
                            <div>
                                <strong>{isProject ? 'Projekt' : 'Ärende'}:</strong> {taskTitle}
                            </div>
                            {buildingName && (
                                <div>
                                    <strong>Byggnad:</strong> {buildingName}
                                </div>
                            )}
                        </div>
                        <div className="small mb-2">
                            <Badge bg="primary">{linkLabel}</Badge>
                            <span className="text-muted ms-2">— länken genereras automatiskt</span>
                        </div>
                        <div className="small mb-2">
                            Din personliga länk visar alla dina uppgifter – spara den som bokmärke:{' '}
                            <span className="text-muted">länk till översikten</span>
                        </div>
                        <div className="text-muted" style={{ fontSize: '11px' }}>
                            Detta mail skickades från Molnfastigheter Sverige AB.
                        </div>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>Svarsadress</Form.Label>
                        <Form.Control
                            type="email"
                            value={options.replyTo}
                            onChange={(e) => onReplyToChange(e.target.value)}
                            placeholder={loadingDefault ? 'Hämtar din aviseringsadress…' : 'namn@exempel.se'}
                            disabled={disabled}
                        />
                        <Form.Text className="text-muted">
                            Om mottagaren skulle svara på e-posten går svaret hit. Det är inte
                            det förväntade arbetssättet – bara en reserv ifall de svarar.
                        </Form.Text>
                    </Form.Group>
                </div>
            </Collapse>

            <Form.Check
                type="checkbox"
                id={`assignment-send-copy-${idSuffix}`}
                label="Skicka en kopia till mig"
                checked={options.sendCopyToAdmin}
                onChange={(e) => onSendCopyToAdminChange(e.target.checked)}
                disabled={disabled}
            />
        </>
    );
};

export default AssignmentEmailComposer;
